import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Alert, Autocomplete, Button, TextField } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

import { useDispatch, useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import moment from "moment";
import toast from "react-hot-toast";

import { impairInvestment } from "../../actions/impairment";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  borderRadius: "25px",
  transform: "translate(-50%, -50%)",
  width: 760,
  bgcolor: "white",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const columns = [
  { id: "code", label: "Code", minWidth: 100 },
  { id: "investment_type", label: "Investment type", minWidth: 200 },
  { id: "regulator", label: "Regulator", minWidth: 170, align: "right" },
  {
    id: "cost",
    label: "Cost",
    minWidth: 170,
    align: "right",
    format: (value) => value.toLocaleString("en-US"),
  },
  { id: "rate", label: "Rate", minWidth: 170, align: "right" },
  {
    id: "impaired_amount",
    label: "Impaired Amount",
    minWidth: 170,
    align: "right",
  },
  {
    id: "impairment_date",
    label: "Impairment date",
    minWidth: 170,
    align: "right",
  },
  {
    id: "maturity_date",
    label: "Maturity date",
    minWidth: 170,
    align: "right",
  },
];

function createData(
  id,
  code,
  investment_type,
  regulator,
  cost,
  rate,
  impaired_amount,
  impairment_date,
  maturity_date,
) {
  return {
    id,
    code,
    investment_type,
    regulator,
    cost,
    rate,
    impaired_amount,
    impairment_date,
    maturity_date,
  };
}

function safeText(v) {
  return String(v ?? "").trim();
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("en-US");
}

function fmtDate(v) {
  if (!v) return "-";
  const m = moment(v);
  return m.isValid() ? m.format("DD-MMM-YYYY") : "-";
}

export default function Impairment() {
  const dispatch = useDispatch();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const impaired = useSelector((state) => state.impaired);
  const investments = useSelector((state) => state.investments);
  const undefinedInvestments = useSelector(
    (state) => state.approvedUndefinedInvestments,
  );

  const allInvestments = React.useMemo(
    () => [...(investments || []), ...(undefinedInvestments || [])],
    [investments, undefinedInvestments],
  );

  const user = JSON.parse(secureLocalStorage.getItem("profile") || "{}");

  const [data, setData] = React.useState(impaired?.data || []);

  React.useEffect(() => {
    setData(impaired?.data || []);
  }, [impaired?.data]);

  const [investmentData, setInvestmentData] = React.useState({
    code: "",
    impaired_amount: "",
    impairment_date: "",
    impaired: "true",
    impaired_by:
      `${user?.results?.firstName || ""} ${user?.results?.lastName || ""}`.trim(),
  });

  // selected investment details
  const selectedInvestment = React.useMemo(() => {
    if (!investmentData.code) return null;
    return (
      allInvestments.find((inv) => inv?.code === investmentData.code) || null
    );
  }, [allInvestments, investmentData.code]);

  const selectedId = selectedInvestment?._id || "";

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const rows = (data || []).map((d) =>
    createData(
      d._id,
      d.code,
      d.investment_type,
      d.regulator,
      d.cost,
      d.rate,
      d.impaired_amount,
      moment(d.impairment_date).format("DD-MM-YYYY"),
      moment(d.maturity_date).format("DD-MM-YYYY"),
    ),
  );

  const options = React.useMemo(() => {
    return (allInvestments || []).map((inv) => inv?.code).filter(Boolean);
  }, [allInvestments]);

  function impair() {
    if (
      investmentData.code &&
      investmentData.impaired_amount &&
      investmentData.impairment_date &&
      selectedId
    ) {
      dispatch(impairInvestment(selectedId, investmentData));
      handleClose();
      setInvestmentData((prev) => ({
        ...prev,
        code: "",
        impaired_amount: "",
        impairment_date: "",
      }));
    } else {
      toast.error("Please fill in required fields");
    }
  }

  return (
    <div>
      <Paper sx={{ width: "100%" }}>
        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          open={open}
          onClose={handleClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 500 } }}
        >
          <Fade in={open}>
            <Box sx={style}>
              <Typography
                id="transition-modal-title"
                variant="h5"
                component="h2"
                style={{ marginBottom: 15 }}
              >
                Impair Investment
              </Typography>

              <form
                style={{
                  display: "flex",
                  flexDirection: "row",
                  columnGap: 20,
                  justifyContent: "space-around",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <Autocomplete
                    disablePortal
                    options={options}
                    value={investmentData.code || null}
                    onChange={(e, n) =>
                      setInvestmentData({ ...investmentData, code: n || "" })
                    }
                    sx={{ width: "100%", maxWidth: 420, mb: 1 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Code" required />
                    )}
                  />

                  {/* ✅ SHOW INVESTMENT INFO HERE (BEFORE IMPAIRMENT) */}
                  {selectedInvestment ? (
                    <Paper
                      variant="outlined"
                      style={{
                        padding: 14,
                        borderRadius: 14,
                        marginBottom: 12,
                        background: "#fafafa",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 800 }}
                      >
                        Investment Summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review the details below to confirm you are impairing
                        the correct investment.
                      </Typography>

                      <Divider style={{ marginTop: 10, marginBottom: 10 }} />

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 10,
                        }}
                      >
                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Code
                          </Typography>
                          <Typography style={{ fontWeight: 700 }}>
                            {safeText(selectedInvestment.code) || "-"}
                          </Typography>
                        </div>

                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Investment Type
                          </Typography>
                          <Typography style={{ fontWeight: 700 }}>
                            {safeText(selectedInvestment.investment_type) ||
                              "-"}
                          </Typography>
                        </div>

                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Institution
                          </Typography>
                          <Typography style={{ fontWeight: 700 }}>
                            {safeText(selectedInvestment.institution) || "-"}
                          </Typography>
                        </div>

                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Tenure
                          </Typography>
                          <Typography style={{ fontWeight: 700 }}>
                            {selectedInvestment.tenure ?? "-"}
                          </Typography>
                        </div>

                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Cost
                          </Typography>
                          <Typography style={{ fontWeight: 800 }}>
                            {fmtMoney(selectedInvestment.cost)}
                          </Typography>
                        </div>

                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Total Interest
                          </Typography>
                          <Typography style={{ fontWeight: 800 }}>
                            {fmtMoney(selectedInvestment.total_interest)}
                          </Typography>
                        </div>

                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Purchase Date
                          </Typography>
                          <Typography style={{ fontWeight: 700 }}>
                            {fmtDate(selectedInvestment.purchase_date)}
                          </Typography>
                        </div>

                        <div>
                          <Typography variant="caption" color="text.secondary">
                            Maturity Date
                          </Typography>
                          <Typography style={{ fontWeight: 700 }}>
                            {fmtDate(selectedInvestment.maturity_date)}
                          </Typography>
                        </div>
                      </div>
                    </Paper>
                  ) : investmentData.code ? (
                    <Alert severity="warning" style={{ marginBottom: 12 }}>
                      Investment details not found for the selected code.
                    </Alert>
                  ) : null}

                  <TextField
                    style={{ marginBottom: 10, maxWidth: 420 }}
                    type="number"
                    size="small"
                    label="Amount"
                    required
                    value={investmentData.impaired_amount}
                    onChange={(e) =>
                      setInvestmentData({
                        ...investmentData,
                        impaired_amount: e.target.value,
                      })
                    }
                  />

                  <TextField
                    style={{ marginBottom: 10, maxWidth: 420 }}
                    type="date"
                    size="small"
                    label="Impairment Date"
                    value={investmentData.impairment_date}
                    onChange={(e) =>
                      setInvestmentData({
                        ...investmentData,
                        impairment_date: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                    required
                  />

                  <div>
                    <Button
                      onClick={impair}
                      variant="contained"
                      size="small"
                      style={{ textTransform: "none", margin: 5 }}
                    >
                      Submit
                    </Button>

                    <Button
                      variant="contained"
                      style={{
                        textTransform: "none",
                        backgroundColor: "#D32F2F",
                      }}
                      onClick={handleClose}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            </Box>
          </Fade>
        </Modal>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Button
            size="small"
            style={{ textTransform: "capitalize", margin: 5 }}
            onClick={handleOpen}
            variant="contained"
          >
            Impair Investment
          </Button>
        </div>

        {impaired?.error && <Alert severity="error">{impaired.error}</Alert>}
        {impaired?.success && (
          <Alert severity="success">{impaired.success}</Alert>
        )}

        <TableContainer sx={{ maxHeight: 740 }}>
          <Table size="small" stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      top: 0,
                      minWidth: column.minWidth,
                      backgroundColor: "#f8f8f8",
                      color: "#000",
                      fontWeight: "bold",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format && typeof value === "number"
                            ? column.format(value)
                            : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
